import { useState, useEffect } from 'react';
import { fetchCurrentPatient } from '../utils/api';
import { validateUserData } from '../utils/helpers';
import { usePodIP } from './usePodIP';

/**
 * Custom hook for managing user data
 * @returns {Object} - User data state and methods
 */
export const useUserData = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { podIP } = usePodIP();

  useEffect(() => {
    if (podIP) {
      const loadUserData = async () => {
        try {
          setLoading(true);
          setError(null);

          // Fetch current patient data (includes fallback to mock data)
          const data = await fetchCurrentPatient(podIP);
          console.log('Fetched patient data from current_patient API:', data);

          if (validateUserData(data)) {
            setUserData(data);
          } else {
            throw new Error('Invalid user data received');
          }
        } catch (err) {
          console.error('Error loading user data:', err);
          setError(err.message);
          // Set fallback data with better structure
          setUserData({
            id: 'mock-user-123',
            name: 'Guest',
            age: 30,
            gender: 'Male',
            email: 'guest@example.com',
            isMockData: true,
            patientId: '68',
            sessionId: '123',
            requestId: '123',
            podIP: podIP
          });
        } finally {
          setLoading(false);
        }
      };

      loadUserData();
    }
  }, [podIP]);

  const updateUserData = (newData) => {
    setUserData(prev => ({ ...prev, ...newData }));
  };

  const refreshUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch current patient data (includes fallback to mock data)
      const data = await fetchCurrentPatient(podIP);

      if (validateUserData(data)) {
        setUserData(data);
      } else {
        throw new Error('Invalid user data received');
      }
    } catch (err) {
      console.error('Error refreshing user data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    userData,
    loading,
    error,
    updateUserData,
    refreshUserData
  };
};
